<template>
  <div class="multi-store-drawer-trigger">
    <transition name="fade">
      <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
      <img
        v-if="flag"
        :key="`flag-${currentCountryCode}`"
        alt=""
        class="multi-store-drawer-trigger__flag"
        role="presentation"
        :src="flag.src"
        :srcset="flag.srcset"
      >
    </transition>

    <div class="multi-store-drawer-trigger___buttons">
      <button
        ref="dropdown"
        class="multi-store-drawer-trigger___button"
        :label="getCurrentCountryName()"
        modifiers="text"
        type="button"
        @click="toggleDropdown"
      >
        {{ getCurrentCountryName() }}
        <sharp-down
          v-if="!isMobile"
          class="sharp-down-icon"
          :class="{ 'rotated': isDropdownVisible }"
        />
      </button>
      <div
        v-if="isDropdownVisible && !isMobile"
        class="multi-store-drawer-trigger__dropdown"
        role="region"
      >
        <ul>
          <li
            v-for="country of countries"
            :key="country.code"
            class="multi-store-drawer-trigger__dropdown-item"
          >
            <img
              v-if="country"
              :key="`flag-${country.code.toLowerCase()}`"
              class="multi-store-drawer-trigger__flag"
              role="presentation"
              :src="`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`"
              :srcset="`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 40w, https://flagcdn.com/w80/${country.code.toLowerCase()}.png 80w, https://flagcdn.com/w160/${country.code.toLowerCase()}.png 160w`"
              alt=""
            >
            <button
              class="multi-store-drawer-trigger__dropdown-button"
              type="button"
              @click="selectCountry(country)"
            >
              {{ country.name }}
            </button>
          </li>
        </ul>
      </div>

      <!-- Overlay for Mobile-country -->
      <div
        v-if="isDropdownVisible && isMobile"
        class="multi-store-drawer-trigger__overlay"
      >
        <div class="multi-store-drawer-trigger__overlay__header">
          <button
            class="multi-store-drawer-trigger__overlay__close"
            type="button"
            @click="closeOverlay"
          >
            <new-close />
          </button>
        </div>
        <ul class="multi-store-drawer-trigger__overlay__list">
          <li
            v-for="country of countries"
            :key="country.code"
            class="multi-store-drawer-trigger__overlay__item"
          >
            <img
              v-if="country"
              :key="`flag-${country.code.toLowerCase()}`"
              class="multi-store-drawer-trigger__flag"
              role="presentation"
              :src="`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`"
              :srcset="`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 40w, https://flagcdn.com/w80/${country.code.toLowerCase()}.png 80w, https://flagcdn.com/w160/${country.code.toLowerCase()}.png 160w`"
              alt=""
            >
            <button
              class="multi-store-drawer-trigger__overlay__button"
              type="button"
              @click="selectCountry(country)"
            >
              {{ country.name }}
            </button>
          </li>
        </ul>
      </div>

      <div class="multi-store-drawer-trigger">
        <!-- Language Button -->
        <button
          ref="languageDropdown"
          class="multi-store-drawer-trigger___button-language"
          :label="getLanguageName()"
          modifiers="text"
          type="button"
          @click="toggleLanguageDropdown"
        >
          {{ getLanguageName() }}
          <sharp-down
            v-if="!isMobile"
            class="sharp-down-icon"
            :class="{ 'rotated': isLanguageDropdownVisible }"
          />
        </button>
        <!-- Language Dropdown -->
        <div
          v-if="isLanguageDropdownVisible && !isMobile"
          class="multi-store-drawer-trigger__dropdown__language"
          role="region"
        >
          <ul>
            <li
              v-for="language of languages"
              :key="language.code"
              class="multi-store-drawer-trigger__dropdown-item"
            >
              <button
                class="multi-store-drawer-trigger__dropdown-button"
                type="button"
                @click="selectLanguage(language)"
              >
                {{ language.name }}
              </button>
            </li>
          </ul>
        </div>

        <!-- Language Overlay for mobile -->
        <div
          v-if="isLanguageDropdownVisible && isMobile"
          class="multi-store-drawer-trigger__overlay"
        >
          <div class="multi-store-drawer-trigger__overlay__header">
            <button
              class="multi-store-drawer-trigger__overlay__close"
              type="button"
              @click="closeLanguageOverlay"
            >
              <new-close />
            </button>
          </div>
          <ul class="multi-store-drawer-trigger__overlay__list">
            <li
              v-for="language of languages"
              :key="language.code"
              class="multi-store-drawer-trigger__overlay__item"
            >
              <button
                class="multi-store-drawer-trigger__overlay__button"
                type="button"
                @click="selectLanguage(language)"
              >
                {{ language.name }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Multi store drawer trigger (multi-store-drawer-trigger)
 * -----------------------------------------------------------------------------
 * Component that displays the current store's flag, name and language which
 * controls opening the multi store drawer.
 *
 * @param {String} namespace - Namespace of the overlay this element controls.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import SharpDown from '~icons/directional-navigation/arrow/sharp-down.svg'
import NewClose from '~icons/directional-navigation/arrow/new-close.svg'

export default {
  name: 'MultiStoreDrawerTrigger',

  components: {
    SharpDown,
    NewClose,
  },

  props: {
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      isExpanded: false,
      isDropdownVisible: false,
      isLanguageDropdownVisible: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      isMobile: (state) => state.index.screen.mobile,
      countries: (state) => {
        // Extract countries from the multi-store Vuex state
        const supported = state['multi-store'].supported || {}
        return Object.values(supported).map((value) => value.country)
      },
      currentCountryCode: (state) => state['multi-store']?.selectedStore?.country?.code || 'US',
      languages(state) {
        return (
          state['multi-store'].supported[this.currentCountryCode]?.languages ||
          []
        )
      },
      selectedLanguage() {
        return (
          this.$store.state['multi-store']?.selectedStore?.language ||
          { code: 'EN', name: 'English' }
        )
      },
    }),

    /**
     * Compute country display text.
     * @returns {String}
     */
    countryText() {
      return this.isMobile
        ? this.getCurrentCountryName()
        : this.$string('general.multi_store.drawer.country_trigger')
          .replace('{{ country_name }}', this.getCurrentCountryName())
    },

    /**
     * Compute flag URL.
     * @returns {Object}
     */
    flag() {
      const code = this.currentCountryCode?.toLowerCase()
      if (!code) {
        return false
      }

      return {
        src: `https://flagcdn.com/w40/${code}.png`,
        srcset: `https://flagcdn.com/w40/${code}.png 40w, https://flagcdn.com/w80/${code}.png 80w, https://flagcdn.com/w160/${code}.png 160w`,
      }
    },

    /**
     * Compute language display text.
     * @returns {String}
     */
    languageText() {
      return this.isMobile
        ? this.$variable('store.languageName')
        : this.$string('general.multi_store.drawer.language_trigger')
          .replace('{{ language_name }}', this.$variable('store.languageName'))
    },
  },

  watch: {

    /**
     * Watch active overlay set aria expanded values.
     */
    getActiveOverlay() {
      this.isExpanded = this.getActiveOverlay?.namespace === this.namespace
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      setSelectedStore: 'multi-store/setSelectedStore',
      setPreference: 'multi-store/setPreference',

    }),

    /**
     * Opens the multi store drawer.
     */
    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible
      this.isLanguageDropdownVisible = false
    },

    toggleLanguageDropdown() {
      this.isLanguageDropdownVisible = !this.isLanguageDropdownVisible
      this.isDropdownVisible = false

    },

    closeOverlay() {
      this.isDropdownVisible = false
    },
    closeLanguageOverlay() {
      this.isLanguageDropdownVisible = false
    },

    selectCountry(country) {
      this.setSelectedStore({
        country,
        instance: this.$store.state['multi-store'].supported[country.code]?.instance,
        language: this.$store.state['multi-store'].supported[country.code]?.languages[0],
      })
      this.setPreference({
        country,
        language: this.$store.state['multi-store'].supported[country.code]?.languages[0],
      })
      this.isDropdownVisible = false
    },

    getCurrentCountryName() {
      const country = this.countries.find(
        (countryee) => {
          return (
            countryee.code.toLowerCase() === this.currentCountryCode.toLowerCase()
          )
        },
      )
      return country
        ? `${this.isMobile ? country.name.toUpperCase() : `country : ${country.name}`}`
        : 'Select a Country'
    },

    selectLanguage(language) {
      this.setSelectedStore({
        country: this.$store.state['multi-store'].supported[
          this.currentCountryCode
        ].country,
        instance: this.$store.state['multi-store'].supported[
          this.currentCountryCode
        ].instance,
        language,
      })
      this.isLanguageDropdownVisible = false
    },

    getLanguageName() {
      return this.selectedLanguage
        ? `${this.isMobile ? this.selectedLanguage.name.toUpperCase() : `language : ${this.selectedLanguage.name}`}`
        : 'Select a Language'
    },
    handleClickOutside(event) {
      const dropdown = this.$refs.dropdown
      const languageDropdown = this.$refs.languageDropdown
      if (dropdown && !dropdown.contains(event.target)) {
        this.isDropdownVisible = false
      }
      if (languageDropdown && !languageDropdown.contains(event.target)) {
        this.isLanguageDropdownVisible = false
      }
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-drawer-trigger';
</style>
